import React from "react";

import holidayHero from "../../assets/Images/roots/holiday-hero.webp";
import holidayGiftGuide from "../../assets/Images/roots/holiday-gift-guide.webp";
import shoppableHotspots from "../../assets/Images/roots/shoppable-hotspots.webp";
import apresSki from "../../assets/Images/roots/apres-ski.webp";
import merchandisingCarousel from "../../assets/Images/roots/merchandising-carousel.webp";
import fabricExploration from "../../assets/Images/roots/fabric-exploration.webp";
import holidayHomepage from "../../assets/Images/roots/holiday-homepage.webp";

const screenshots = {
  homepage: {
    title: "Holiday 2026 homepage",
    src: holidayHomepage,
    alt: "Roots holiday homepage hero with winter campaign imagery and shopping links",
  },
  destination: {
    title: "Holiday destination",
    src: holidayHero,
    alt: "Roots seasonal destination page with gift-guide imagery and shopping links",
    caption: "I built the responsive hero and shopping paths within the Holiday campaign.",
  },
  giftGuide: {
    title: "Gift guide",
    src: holidayGiftGuide,
    alt: "Roots holiday gift guide with horizontally arranged shopping categories",
    caption: "I built a horizontal category carousel linking the campaign to gift destinations.",
  },
  editorial: {
    title: "Après Ski editorial",
    src: apresSki,
    alt: "Après Ski Approved page with winter editorial imagery and shopping sections",
    caption: "I implemented this image-led destination with shoppable merchandising sections.",
  },
  hotspots: {
    title: "Shoppable editorial",
    src: shoppableHotspots,
    alt: "Editorial photo with a selected product hotspot, product information overlay and colour options",
    caption: "I worked on the hotspot layout and product overlay shown within the editorial image.",
  },
  carousel: {
    title: "Merchandising carousel",
    src: merchandisingCarousel,
    alt: "Roots gift guide showing a horizontal carousel of shopping categories",
    caption: "I worked on the responsive category rail and its narrow-screen overflow.",
  },
  fabric: {
    title: "Fabric exploration",
    src: fabricExploration,
    alt: "Roots fabric discovery interface with category controls and colour swatches",
    caption: "I worked on category and colour-selection UI for product discovery.",
  },
};

const Screenshot = ({ shot, eager = false, full = false }) => (
  <figure className={`roots-shot${full ? " roots-shot--full" : ""}`}>
    <a
      className="roots-shot__link"
      href={shot.src}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open full-size screenshot: ${shot.title} (new tab)`}
    >
      <img src={shot.src} alt={shot.alt} loading={eager ? "eager" : "lazy"} decoding="async" />
    </a>
    <figcaption>
      <strong>{shot.title}</strong>
      {shot.caption && <span>{shot.caption}</span>}
    </figcaption>
  </figure>
);

const Topic = ({ title, children }) => (
  <div className="roots-topic">
    <h5>{title}</h5>
    <p>{children}</p>
  </div>
);

export const rootsCaseStudySlides = [
  {
    label: "Overview",
    content: (
      <section className="case-study-slide roots-slide" aria-labelledby="roots-overview">
        <h4 id="roots-overview">Front-End Development at Roots</h4>
        <div className="roots-split">
          <div className="roots-slide__copy">
            <p className="roots-slide__role">Front-End Developer | March 2024–Present</p>
            <p>
              I work on the front end of Roots' e-commerce storefront, developing seasonal
              campaigns, interactive shopping experiences and improvements to existing site
              functionality.
            </p>
            <p>
              In Salesforce Commerce Cloud, I use JavaScript, HTML and SCSS to turn designs into
              production-ready experiences across desktop and mobile. I also troubleshoot live
              functionality and work with designers, developers and QA to ship updates.
            </p>
            <ul className="project-tags project-tags--modal" aria-label="Roots technology">
              <li>Salesforce Commerce Cloud</li>
              <li>JavaScript</li>
              <li>HTML</li>
              <li>SCSS</li>
            </ul>
          </div>
          <Screenshot shot={screenshots.homepage} eager />
        </div>
      </section>
    ),
  },
  {
    label: "Engineering & Responsibilities",
    content: (
      <section className="case-study-slide roots-slide" aria-labelledby="roots-role">
        <h4 id="roots-role">Engineering &amp; Responsibilities</h4>
        <div className="roots-topic-grid">
          <Topic title="Front-End Development">
            I build campaign pages and components with JavaScript, HTML and SCSS, including
            carousels, video and shoppable content.
          </Topic>
          <Topic title="Salesforce Commerce Cloud">
            I implement work through Page Designer and content assets within the existing
            storefront.
          </Topic>
          <Topic title="Collaboration & Delivery">
            I work from Figma designs, coordinate with UX and QA, and resolve issues across
            devices before release.
          </Topic>
          <Topic title="Accessibility & Localization">
            I improve semantic markup, keyboard interactions and form accessibility, and support
            English and French storefront experiences.
          </Topic>
        </div>
      </section>
    ),
  },
  {
    label: "Campaign Development",
    content: (
      <section className="case-study-slide roots-slide" aria-labelledby="roots-campaigns">
        <h4 id="roots-campaigns">Campaign Development</h4>
        <p className="roots-slide__lead">
          For Holiday 2026, I built responsive campaign modules for the homepage and destination
          pages, combining video and image treatments, promotional messaging and category links.
        </p>
        <div className="roots-shot-grid roots-shot-grid--two">
          <Screenshot shot={screenshots.destination} full />
          <Screenshot shot={screenshots.giftGuide} />
          <Screenshot shot={screenshots.editorial} />
        </div>
      </section>
    ),
  },
  {
    label: "Interactive Commerce",
    content: (
      <section className="case-study-slide roots-slide" aria-labelledby="roots-commerce">
        <h4 id="roots-commerce">Interactive Commerce</h4>
        <p className="roots-slide__lead">
          Alongside campaigns, I work on interfaces that connect editorial content with product
          discovery, including shoppable imagery, category rails and colour-led browsing.
        </p>
        <div className="roots-shot-grid roots-shot-grid--two">
          <Screenshot shot={screenshots.hotspots} full />
          <Screenshot shot={screenshots.carousel} />
          <Screenshot shot={screenshots.fabric} />
        </div>
      </section>
    ),
  },
  {
    label: "Production & Delivery",
    content: (
      <section className="case-study-slide roots-slide" aria-labelledby="roots-delivery">
        <h4 id="roots-delivery">Production &amp; Delivery</h4>
        <p className="roots-slide__lead">
          On a live storefront, I spend time maintaining what is already there as well as building
          new work. That means investigating issues, testing changes and working within Salesforce
          Commerce Cloud's release process.
        </p>
        <div className="roots-topic-grid">
          <Topic title="Debugging & Accessibility">
            I fix JavaScript and layout issues and improve accessibility in customer-facing flows,
            including checkout.
          </Topic>
          <Topic title="QA & Release">
            I test changes in staging, address QA feedback and support production releases with
            UX, development and merchandising teams.
          </Topic>
          <Topic title="Bilingual Storefront">
            I support English and French campaign implementations, checking that content and
            interactions work in both storefronts.
          </Topic>
        </div>
      </section>
    ),
  },
];
